'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedBigInt } from '@/components/ui/animated-bigint';
import { AnimatedBigIntOnMount } from '@/components/ui/animated-bigint-on-mount';
import { AnimatedButton } from '@/components/ui/animated-button';
import type { DropdownEntry } from '@/components/ui/app-dropdown-menu';
import { AppDropdownMenu } from '@/components/ui/app-dropdown-menu';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { ArrowDown01Icon } from '@/components/ui/icon/glyphs';
import { useUserPortfolioSummary } from '@/hooks/positions/user-portfolio/selectors';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
interface PortfolioCurrentSummaryProps {
  readonly userId: string;
  readonly walletPubkeys: readonly string[];
  readonly viewerMode: 'own' | 'other';
  readonly pickerTriggerLabel: string;
  readonly pickerItems: ReadonlyArray<DropdownEntry>;
  readonly showWalletPicker: boolean;
  readonly deltaD6: bigint | null;
  readonly deltaPercentTenths: bigint | null;
  readonly deltaFullWindow: boolean;
}
export function PortfolioCurrentSummary(t0) {
  const $ = _c(61);
  const {
    userId,
    walletPubkeys,
    viewerMode,
    pickerTriggerLabel,
    pickerItems,
    showWalletPicker,
    deltaD6,
    deltaPercentTenths,
    deltaFullWindow
  } = t0;
  const t = useTranslations("profile.next.pnl_card");
  const tStats = useTranslations("profile.stats");
  const tPicker = useTranslations("profile.next.picker");
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t1 = {
      userId,
      wallets: walletPubkeys
    };
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const summary = useUserPortfolioSummary(t1);
  if (summary.isPending) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="col-start-1 row-start-1 flex w-max flex-col gap-1" data-testid="portfolio-current-summary-loading"><Skeleton className="h-3 w-12" /><Skeleton className="h-12 max-w-[min(100%,12rem)] w-48" /><Skeleton className="h-4 max-w-[min(100%,10rem)] w-40" /></div>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <>{t2}<div className="grid grid-cols-3 gap-2 col-span-2 row-start-2"><StatTileSkeleton /><StatTileSkeleton /><StatTileSkeleton /></div></>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t2;
  if ($[5] !== formatD6) {
    t2 = function formatTotal(valueD6) {
      const [integerStr, decimalStr] = splitDecimal(formatD6(valueD6.toString(), "full"));
      return <><span className="text-display-sm font-semibold tracking-tight text-foreground-primary">{integerStr}</span>{decimalStr.length > 0 ? <span className="text-heading-lg font-semibold tracking-tight text-foreground-tertiary">{decimalStr}</span> : null}</>;
    };
    $[5] = formatD6;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const formatTotal = t2;
  let t3;
  if ($[7] !== pickerItems || $[8] !== pickerTriggerLabel || $[9] !== showWalletPicker || $[10] !== tPicker || $[11] !== viewerMode) {
    t3 = viewerMode === "own" && showWalletPicker ? <AppDropdownMenu align="start" trigger={<AnimatedButton variant="tertiary" size="xs" className="-ml-1 self-start px-1 tracking-wide hover:bg-transparent" aria-label={tPicker("trigger_label")} data-testid="portfolio-pnl-card-wallet-picker-trigger"><span className="truncate">{pickerTriggerLabel}</span><Icon icon={ArrowDown01Icon} className="size-3.5" aria-hidden="true" /></AnimatedButton>} items={pickerItems} /> : <span className="-ml-1 self-start truncate px-1 text-caption tracking-wide text-foreground-secondary" data-testid="portfolio-pnl-card-wallet-picker-label">{pickerTriggerLabel}</span>;
    $[7] = pickerItems;
    $[8] = pickerTriggerLabel;
    $[9] = showWalletPicker;
    $[10] = tPicker;
    $[11] = viewerMode;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t) {
    t4 = t("total_label");
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== formatTotal || $[16] !== summary.totalD6) {
    t5 = summary.totalD6 === null ? <span className="text-display-sm font-semibold tracking-tight text-foreground-primary">{formatTotal(BigInt(0))}</span> : <AnimatedBigInt value={summary.totalD6} format={formatTotal} className="contents" />;
    $[15] = formatTotal;
    $[16] = summary.totalD6;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t4 || $[19] !== t5) {
    t6 = <fieldset className="m-0 flex w-max items-baseline gap-0.5 whitespace-nowrap border-0 p-0" data-testid="portfolio-pnl-card-total" aria-label={t4}>{t5}</fieldset>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== deltaD6 || $[22] !== deltaFullWindow || $[23] !== deltaPercentTenths || $[24] !== formatD6Signed || $[25] !== t) {
    t7 = deltaD6 !== null ? <p className={cn("text-body tabular-nums", deltaToneClass(deltaD6))} data-testid="portfolio-pnl-card-delta"><span><AnimatedBigIntOnMount value={deltaD6} format={valueD6_0 => formatD6Signed(valueD6_0.toString())} />{deltaPercentTenths === null ? null : <AnimatedBigIntOnMount value={deltaPercentTenths} format={_temp} />}</span><span className="ml-1 text-foreground-tertiary">{deltaFullWindow ? t("last_day") : t("all_time")}</span></p> : null;
    $[21] = deltaD6;
    $[22] = deltaFullWindow;
    $[23] = deltaPercentTenths;
    $[24] = formatD6Signed;
    $[25] = t;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t3 || $[28] !== t6 || $[29] !== t7) {
    t8 = <div className="col-start-1 row-start-1 flex w-max flex-col gap-1">{t3}{t6}{t7}</div>;
    $[27] = t3;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== tStats) {
    t9 = tStats("cash");
    $[31] = tStats;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== formatD6) {
    t10 = valueD6_1 => formatD6(valueD6_1.toString(), "compact");
    $[33] = formatD6;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== summary.cashD6 || $[36] !== t10 || $[37] !== t9) {
    t11 = <StatTile testId="stat-tile-cash" label={t9} value={summary.cashD6} format={t10} />;
    $[35] = summary.cashD6;
    $[36] = t10;
    $[37] = t9;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== tStats) {
    t12 = tStats("holdings");
    $[39] = tStats;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== formatD6) {
    t13 = valueD6_2 => formatD6(valueD6_2.toString(), "compact");
    $[41] = formatD6;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== summary.holdingsD6 || $[44] !== t12 || $[45] !== t13) {
    t14 = <StatTile testId="stat-tile-holdings" label={t12} value={summary.holdingsD6} format={t13} />;
    $[43] = summary.holdingsD6;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t) {
    t15 = t("positions");
    $[47] = t;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== summary.positionCount) {
    t16 = summary.positionCount === null ? null : BigInt(summary.positionCount);
    $[49] = summary.positionCount;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t15 || $[52] !== t16) {
    t17 = <StatTile testId="stat-tile-positions" label={t15} value={t16} format={formatWholeNumber} />;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== t11 || $[55] !== t14 || $[56] !== t17) {
    t18 = <div className="grid grid-cols-3 gap-2 col-span-2 row-start-2">{t11}{t14}{t17}</div>;
    $[54] = t11;
    $[55] = t14;
    $[56] = t17;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== t18 || $[59] !== t8) {
    t19 = <>{t8}{t18}</>;
    $[58] = t18;
    $[59] = t8;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  return t19;
}
function _temp(value) {
  return ` (${formatPercentageTenths(value)})`;
}
function StatTile(t0) {
  const $ = _c(9);
  const {
    label,
    value,
    format,
    testId
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-caption text-foreground-tertiary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== format || $[3] !== value) {
    t2 = value === null ? <span className="text-body-lg font-medium tabular-nums text-foreground-primary">{format(BigInt(0))}</span> : <AnimatedBigIntOnMount value={value} format={format} className="text-body-lg font-medium text-foreground-primary" />;
    $[2] = format;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== testId) {
    t3 = <div className="flex flex-1 flex-col gap-0.5 rounded-panel-inset border border-border-secondary bg-background-primary px-3 py-2.5 sm:px-4 sm:py-3" data-testid={testId}>{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = testId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function StatTileSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-1 flex-col gap-0.5 rounded-panel-inset border border-border-secondary bg-background-primary px-3 py-2.5 sm:px-4 sm:py-3"><Skeleton className="h-3 w-12" /><Skeleton className="mt-0.5 h-6 w-14 max-w-full" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function splitDecimal(formatted: string): [string, string] {
  const match = formatted.match(/^(.+)([.,]\d+)$/);
  if (match === null || match[1] === undefined || match[2] === undefined) return [formatted, ''];
  return [match[1], match[2]];
}
function deltaToneClass(deltaD6: bigint): string {
  if (deltaD6 > BigInt(0)) return 'text-data-profit';
  if (deltaD6 < BigInt(0)) return 'text-data-loss';
  return 'text-foreground-tertiary';
}
function formatPercentageTenths(value: bigint): string {
  const sign = value > BigInt(0) ? '+' : value < BigInt(0) ? '-' : '';
  const magnitude = value < BigInt(0) ? -value : value;
  return `${sign}${magnitude / BigInt(10)}.${magnitude % BigInt(10)}%`;
}
function formatWholeNumber(value: bigint): string {
  return value.toString();
}